import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import LogoutIcon from "@mui/icons-material/Logout";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import {
  Avatar,
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../../../utils/Functions";
import { Fragment } from "react/jsx-runtime";

export default function InpatientStayList({ pets = [], data = null }) {
  const { t } = useTranslation();

  if (!data) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary">{t("NoStaysRecorded")}</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <VetCard name={data.vetFullName} email={data.vetEmail} />
      <Content data={data} pets={pets} />
    </Stack>
  );
}

function VetCard({ name, email }) {
  const { t } = useTranslation();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
        <SupervisedUserCircleIcon />
      </Avatar>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
        >
          {t("ResponsibleVeterinarian")}
        </Typography>
        <Typography variant="body1" fontWeight={700}>
          {name ?? t("N/A")}
        </Typography>
        {email && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <EmailIcon sx={{ fontSize: 13, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {email}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function Content({ data, pets }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        transition: "box-shadow 0.2s",
        "&:hover": {
          boxShadow: 1,
        },
      }}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <DateHeader data={data} />
          <Divider sx={{ mt: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <MedicalDetails data={data} />
        </Grid>
        <Grid item xs={12} md={6}>
          <VaccineTable data={data} />
        </Grid>
      </Grid>
    </Paper>
  );
}

function DateHeader({ data }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14 }} />
            {t("Entrance")}:
          </Typography>
          <Typography variant="body2" fontWeight="medium" color="primary.main">
            {data.dateOfEntrance}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <LogoutIcon sx={{ fontSize: 14 }} />
            {t("Exit")}:
          </Typography>
          <Typography
            variant="body2"
            fontWeight="medium"
            color={data.dateOfExit ? "text.primary" : "warning.main"}
          >
            {data.dateOfExit ? data.dateOfExit : t("Ongoing")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function MedicalDetails({ data }) {
  const { t } = useTranslation();
  return (
    <Fragment>
      <Typography
        variant="subtitle2"
        color="primary"
        gutterBottom
        sx={{ fontWeight: 600, mb: 1.5 }}
      >
        {t("Details")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {data.fivFelv && (
          <MedicalItemCard label="FIV/FELV" value={data.fivFelv} color="info" />
        )}
        <MedicalItemCard
          label={t("Corporal")}
          value={data.corporal}
          isBoolean
        />
        {data.ampoule && (
          <MedicalItemCard
            label={t("Ampoule")}
            value={data.ampoule}
            color="success"
          />
        )}
        {data.antiParasite && (
          <MedicalItemCard
            label={t("AntiParasite")}
            value={data.antiParasite}
            color="warning"
          />
        )}
        {data.dateOfAntiParasite && (
          <MedicalItemCard
            label={t("DateOfAntiParasite")}
            value={data.dateOfAntiParasite}
            color="warning"
          />
        )}
        {data.nextAntiParasiteDate && (
          <MedicalItemCard
            label={t("AntiParasiteNextDate")}
            value={data.nextAntiParasiteDate}
            color="warning"
          />
        )}
      </Box>
    </Fragment>
  );
}

function VaccineTable({ data = [] }) {
  const { t } = useTranslation();

  const sortedVaccines = [...(data.vaccines || [])].sort((a, b) => {
    const dateA = dayjs(a.date, "DD/MM/YYYY");
    const dateB = dayjs(b.date, "DD/MM/YYYY");
    if (!dateA.isValid() && !dateB.isValid()) return 0;
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;
    return dateA.valueOf() - dateB.valueOf();
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 250,
        overflow: "auto",
        boxShadow: "none",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>{t("Category")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("Vaccine")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {t("AdministrationDate")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedVaccines.length > 0 ? (
            sortedVaccines.map((v, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ py: 1 }}>
                  <VaccinesIcon
                    sx={{ fontSize: 20 }}
                    color={v.category == "OTHER" ? "primary" : "error"}
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Tooltip title={v.name} arrow placement="top">
                    <Typography variant="body2">
                      {trimText(v.name, 35)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2">{v.date || t("N/A")}</Typography>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ borderBottom: "none", py: 2 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  {"N/A"}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const MedicalItemCard = ({
  label,
  value,
  color = "info",
  isBoolean = false,
}) => {
  const statusColor = isBoolean ? (value ? "success" : "error") : color;
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        bgcolor: `${statusColor}.50`,
        borderBottom: "1px solid",
        borderColor: `${statusColor}.100`,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: `${statusColor}.700` }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: `${statusColor}.700`,
          wordBreak: "break-all",
          maxWidth: 500,
        }}
      >
        {isBoolean ? (value ? t("Yes") : t("No")) : value}
      </Typography>
    </Box>
  );
};
